import React, { useEffect, useState } from 'react';
import { apiFetch, getApiHost } from '../utils/api';

interface ShillTweet {
  tweetId: string;
  tweetUrl: string;
  tweetText: string;
  authorHandle: string;
  authorFollowers: number;
  engagement: number;
  impressions: number;
  intent: 'promoting' | 'dissuading' | 'neutral';
  createdAt: string;
  recordedAt: string;
}

interface ShillTweetsProps {
  mint: string;
}

const formatRelativeTime = (dateStr: string): string => {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
};

const formatFollowers = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

const INTENT_CONFIG = {
  promoting: {
    label: 'Shilling',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/20',
    icon: 'fas fa-thumbs-down',
    description: 'Accounts actively shilling this token',
  },
  dissuading: {
    label: 'Calling Out Scams',
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/20',
    icon: 'fas fa-thumbs-up',
    description: 'Accounts calling out this token as a scam',
  },
  neutral: {
    label: 'Neutral',
    color: 'text-gray-400',
    bgColor: 'bg-gray-500/10',
    borderColor: 'border-gray-500/20',
    icon: 'fas fa-comment',
    description: 'Mentions without a clear stance',
  },
} as const;

// Deduplicate tweets by authorHandle, keeping the one with highest engagement
const dedupeByAuthor = (tweets: ShillTweet[]): ShillTweet[] => {
  const byAuthor = new Map<string, ShillTweet>();
  for (const t of tweets) {
    const existing = byAuthor.get(t.authorHandle);
    if (!existing || t.engagement > existing.engagement) {
      byAuthor.set(t.authorHandle, t);
    }
  }
  return [...byAuthor.values()];
};

const TweetGroup: React.FC<{ intent: 'promoting' | 'dissuading' | 'neutral'; tweets: ShillTweet[] }> = ({ intent, tweets }) => {
  const config = INTENT_CONFIG[intent];
  const deduped = dedupeByAuthor(tweets);
  // Sort: highest followers first
  const sorted = [...deduped].sort((a, b) => b.authorFollowers - a.authorFollowers);

  return (
    <div className={`${config.bgColor} border ${config.borderColor} rounded-lg p-4`}>
      <div className="flex items-center space-x-2 mb-3">
        <i className={`${config.icon} ${config.color}`}></i>
        <h4 className={`font-semibold ${config.color}`}>{config.label}</h4>
        <span className="text-gray-500 text-sm">({sorted.length} {sorted.length === 1 ? 'account' : 'accounts'})</span>
      </div>
      <p className="text-gray-500 text-xs mb-3">{config.description}</p>
      <div className="space-y-2">
        {sorted.map((tweet) => (
          <div key={tweet.tweetId} className="flex items-start space-x-3 group">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 flex-wrap">
                <a
                  href={`https://x.com/${tweet.authorHandle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-white hover:text-crypto-accent transition-colors"
                >
                  @{tweet.authorHandle}
                </a>
                <span className="text-xs text-gray-500">{formatFollowers(tweet.authorFollowers)} followers</span>
                <span className="text-xs text-gray-600">·</span>
                <span className="text-xs text-gray-500">{formatRelativeTime(tweet.createdAt)}</span>
              </div>
              <p className="text-gray-400 text-sm mt-1 line-clamp-2">{tweet.tweetText}</p>
            </div>
            <a
              href={tweet.tweetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity mt-1"
              title="View on X"
            >
              <img src="/x-logo/logo-white.png" alt="X" className="w-4 h-4" />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

const ShillTweets: React.FC<ShillTweetsProps> = ({ mint }) => {
  const [tweets, setTweets] = useState<ShillTweet[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTweets = async () => {
      try {
        const apiHost = getApiHost();
        const res = await apiFetch(`${apiHost}/api/shill-tweets/${mint}`);
        if (!res.ok) return;
        const data = await res.json();
        setTweets(data.tweets ?? []);
      } catch {
        // Silently fail — section just won't render
      } finally {
        setLoading(false);
      }
    };
    fetchTweets();
  }, [mint]);

  if (loading || tweets.length === 0) return null;

  const promoting = tweets.filter(t => t.intent === 'promoting');
  const dissuading = tweets.filter(t => t.intent === 'dissuading');
  const neutral = tweets.filter(t => t.intent === 'neutral');

  return (
    <div className="bg-crypto-card rounded-xl mb-6">
      <h3 className="text-xl font-bold mb-4 flex items-center">
        <img src="/x-logo/logo-white.png" alt="X" className="w-5 h-5 mr-2 opacity-60" />
        X Mentions
        <span className="ml-2 text-sm font-normal text-gray-500">
          {tweets.length} {tweets.length === 1 ? 'tweet' : 'tweets'} tracked
        </span>
      </h3>

      <div className="space-y-4">
        {promoting.length > 0 && <TweetGroup intent="promoting" tweets={promoting} />}
        {dissuading.length > 0 && <TweetGroup intent="dissuading" tweets={dissuading} />}
        {neutral.length > 0 && <TweetGroup intent="neutral" tweets={neutral} />}
      </div>
    </div>
  );
};

export default ShillTweets;
